import { Controller, useForm } from 'react-hook-form';

import { Footer } from '~shared/ui/footer';
import { ScreenHeader, Screen } from '~shared/ui/screen';

import { PhoneField } from '@breezeventures/ui-kit/phone-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { getMobilePhonePhSchema } from '@breezeventures/web-ph-validators/zod-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

import FlyingManIcon from './icons/flying-man.svg?react';

const TERMS_URL =
  'https://cdn.skyro.ph/web/apps/cash-loan/resources/documents/terms_and_conditions_merchant_portal.pdf';

const phoneFormSchema = z.object({
  phone: getMobilePhonePhSchema({
    required: true,
    requiredMessage: 'Enter phone number',
  }),
});

type PhoneFromValues = z.infer<typeof phoneFormSchema>;

type PhoneScreenProps = {
  initialPhone: string | null;
  onSubmit: (values: PhoneFromValues) => void;
};

export const PhoneScreen = ({ initialPhone, onSubmit }: PhoneScreenProps) => {
  const methods = useForm<PhoneFromValues>({
    mode: 'all',
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phone: initialPhone ?? '',
    },
  });

  return (
    <form
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex h-full flex-col justify-between"
    >
      <Screen
        header={<ScreenHeader />}
        footer={
          <Footer
            submitButtonText="Get&nbsp;a&nbsp;code"
            extraContent={
              <Typography.Text
                color="secondary"
                size="xs"
                className="mt-3 text-center"
              >
                By&nbsp;logging&nbsp;in
                to&nbsp;the&nbsp;Merchant&nbsp;Portal,&nbsp;you&nbsp;hereby&nbsp;agree&nbsp;to
                the&nbsp;Merchant&nbsp;Portal&nbsp;
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Terms&nbsp;and&nbsp;Conditions
                </a>
                .
              </Typography.Text>
            }
          />
        }
      >
        <div className="my-auto flex flex-col items-center">
          <FlyingManIcon />

          <div className="w-full py-8 text-center text-balance">
            <Typography.Title size="s" tag="div">
              Turn&nbsp;
              <span className="text-skyro-blue">every&nbsp;visit</span>{' '}
              into&nbsp;a&nbsp;<span className="text-skyro-blue">sale</span>
            </Typography.Title>

            <Typography.Text color="secondary" size="s" className="mt-3">
              3&nbsp;in&nbsp;10 purchases are&nbsp;made on&nbsp;installments.
              Partner with Skyro and grow your sales up&nbsp;to&nbsp;5x.
              <br />
              Get&nbsp;set&nbsp;up in&nbsp;2&nbsp;days
            </Typography.Text>
          </div>

          <div className="w-full">
            <Controller
              control={methods.control}
              name="phone"
              render={(fp) => (
                <PhoneField
                  {...fp}
                  label="Your mobile number"
                  labelPosition="inner"
                  inputMode="tel"
                  startSection=""
                />
              )}
            />
          </div>
        </div>
      </Screen>
    </form>
  );
};
